import React from 'react';

import {useArticles, useBulkAddArticles, useCategories} from 'app/kbs/api';
import {gt} from 'app/utils/i18n';

import {KBItemList} from './KBItemList';

import type {
  BaseDocumentContentType,
  BulkActionsDocumentType,
  KBCategorySerializedType,
} from 'sweet-types';

interface BulkArticlesCategorySelectionListProps {
  refreshPosition: () => void;
  setParentFolder: (parentFolder: KBCategorySerializedType) => void;
  onSuccess: () => Promise<any>;
  documents: Array<BulkActionsDocumentType>;
  search: string;
  kb_id?: number;
  parent_category_id: string | number;
}

export const BulkArticlesCategorySelectionList = ({
  refreshPosition,
  setParentFolder,
  onSuccess,
  documents,
  search,
  kb_id,
  parent_category_id,
}: BulkArticlesCategorySelectionListProps) => {
  const {data: kbData} = useCategories({
    parent_category_id: search
      ? undefined
      : kb_id != null
      ? parent_category_id
      : undefined,
    search,
    kb_id,
  });

  const {data: articles} = useArticles({
    kb_id: kb_id,
  });

  const [addBulkArticles] = useBulkAddArticles();

  function actionsGetter(
    category: KBCategorySerializedType,
    afterActions: () => Promise<any>
  ) {
    const alreadyInKbCategory = documents.some((doc) =>
      articles?.results.some(
        (article) =>
          article.document_object_id === doc.object_id &&
          article.document_content_type === doc.document_content_type &&
          article.category.id === category.id
      )
    );
    const articleCheck =
      category.content_type === 'category' && !alreadyInKbCategory;
    return [
      {
        text: gt('Add to this category'),
        key: 'add-to-knowledge-base',
        icon: 'fas fa-plus',
        condition: () => articleCheck,
        callback: () =>
          category.content_type === 'category'
            ? addBulkArticles({
                articles: documents.map((doc) => ({
                  knowledgebase_url: category.knowledgebase.url,
                  category_url: category.url,
                  document_content_type:
                    doc.document_content_type as BaseDocumentContentType,
                  document_object_id: doc.object_id,
                })),
              })
                .unwrap()
                .then(onSuccess)
                .then(afterActions)
            : Promise.resolve(true),
      },
    ];
  }

  return (
    <KBItemList
      kbList={kbData?.results}
      itemName={'category'}
      refreshPosition={refreshPosition}
      getItemCaption={(item: KBCategorySerializedType) => {
        if (search) {
          return item.knowledgebase.name;
        }
      }}
      setParentFolder={setParentFolder}
      actionsGetter={actionsGetter}
    />
  );
};
